import React, { memo, useCallback, useMemo } from 'react'

import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'

import { OfferPageActions, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { getTourV2OfferPageBaseUrl } from 'selectors/TourV2/tourV2OfferPageSelectors'
import { pushWithRegion } from 'actions/NavigationActions'
import { sortBy } from 'lib/array/arrayUtils'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'

import TourV2VariationCard from './TourV2VariationCard'
import { tourV2OfferVariationView } from 'actions/AnalyticsActions'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import { tourOptionPackages, tourOptionPackagesEvent } from 'constants/tours'
import { offerEvent } from 'analytics/snowplow/events'
import * as Analytics from 'analytics/analytics'
import { rem } from 'polished'

const VariationsList = styled.div``

interface MappedProps {
  offerPageState: OfferPageState
  baseTourOfferUrl: string
  offer: Tours.V2Offer
  selectedVariation: Tours.V2OfferVariation
  variations: Record<string, Tours.V2OfferVariation>
}

interface Props {
  onVariationChanged: (variationId: string) => void
}

function TourV2Variations(props: Props & MappedProps) {
  const {
    offerPageState,
    baseTourOfferUrl,
    offer,
    selectedVariation,
    variations,
    onVariationChanged,
  } = props

  const offerPageDispatch = useOfferPageDispatch()

  const dispatch = useAppDispatch()

  const selectVariationId = useCallback(
    (variationId: Tours.V2OfferVariation['id'], packageOptionName) => {
      if (selectedVariation.id !== variationId) {
        offerPageDispatch({
          type: OfferPageActions.unselectTourV2OfferDeparture,
        })
        dispatch(pushWithRegion(
          `${baseTourOfferUrl}/${variationId}`,
          '',
          { scrollBehaviour: 'smooth', scrollTo: '#TOUR_V2_CONTENT_TOP_ANCHOR' },
        ))
        dispatch(tourV2OfferVariationView(offer))
        offerPageDispatch({
          type: OfferPageActions.selectTourVOfferVariation,
          variation: offer.variations[variationId],
        })
        onVariationChanged(variationId)
        if (tourOptionPackages.includes(packageOptionName ?? '')) {
          const event = offerEvent(tourOptionPackagesEvent[packageOptionName], offerPageState)
          if (event) Analytics.track('offer-event', event)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedVariation, offerPageDispatch, dispatch, baseTourOfferUrl, onVariationChanged, offer],
  )

  const sortedVariations = useMemo(() => {
    return sortBy(
      Object.values(variations),
      (variation) => findCheapestTourV2PurchasableOption(offer, variation.id)?.price
      , 'asc',
    )
  }, [offer, variations])

  return <VariationsList>
    <CardCarousel width={`min(${rem(460)}, 75%)`}>
      {sortedVariations.map((variationOption, variationOptionIndex) =>
        <TourV2VariationCard
          key={variationOptionIndex + '_1'}
          position={variationOptionIndex + 1}
          offer={offer}
          variation={variationOption}
          isSelected={variationOption.id === selectedVariation.id}
          onVariationSelect={selectVariationId}
        />)
      }
    </CardCarousel>
  </VariationsList>
}

const mapOfferPageStateToProps = (offerPageState: OfferPageState): MappedProps => ({
  offerPageState,
  baseTourOfferUrl: getTourV2OfferPageBaseUrl(offerPageState),
  offer: offerPageState.tourV2Offer,
  selectedVariation: offerPageState.selectedTourV2OfferVariation,
  variations: offerPageState.tourV2Offer?.variations,
})

export default memo(withOfferPageState(mapOfferPageStateToProps)(TourV2Variations))
